import { create } from "zustand";

export enum DeliveryMethods {
  DELIVERY = "delivery",
  PICKUP = "pickup",
}

export type Item = {
  id: string;
  description: string;
  name: string;
  quantity: number;
  price: {
    sale: number;
    subtotal: number;
  };
  image: string;
};

type Delivery = {
  method: DeliveryMethods;
  price: number | null;
};

export type Summary = {
  itemsCount: number;
  total: number;
  subtotal: number;
};

export type Drawer = {
  isOpen: boolean;
};

export type UpsertItemFromProductParams = {
  id: string;
  productName: string;
  productImage?: string;
  price: number;
  quantity: number;
  chosenVariants: Array<{ id: string; name: string }>;
};

type State = {
  drawer: Drawer;
  summary: Summary;
  delivery: Delivery | null;
  items: Array<Item>;
};

type Actions = {
  addItem: (item: Item) => void;
  removeItem: (item: Item) => void;
  updateItem: (item: Item) => void;
  upsertItemFromProduct: (params: UpsertItemFromProductParams) => void;
  clearItems: () => void;
  toggleDrawer: () => void;
  closeDrawer: () => void;
  setDelivery: (delivery: Delivery) => void;
};

const defaultInitState: State = {
  drawer: {
    isOpen: false,
  },
  summary: {
    itemsCount: 0,
    total: 0,
    subtotal: 0,
  },
  delivery: null,
  items: [],
};

const handleSummary = ({ items, summary, delivery }: State): Summary => {
  const subtotal = items.reduce(
    (acc, item) => acc + item.price.sale * item.quantity,
    0
  );

  const itemsCount = items.reduce((acc, item) => acc + item.quantity, 0);
  const shippingCost =
    delivery?.method && delivery.method === DeliveryMethods.DELIVERY
      ? delivery.price || 0
      : 0;

  return {
    ...summary,
    itemsCount,
    subtotal,
    total: subtotal + shippingCost,
  };
};

export const useOrderResumeStore = create<State & Actions>((set) => {
  const initState: State = defaultInitState;
  return {
    ...initState,
    addItem: (item: Item) =>
      set((state) => {
        const items = [...state.items, item];

        return {
          items: items,
          summary: handleSummary({ ...state, items: items }),
        };
      }),
    removeItem: (item: Item) =>
      set((state) => {
        const items = state.items.filter((i) => i.id !== item.id);

        return {
          items: items,
          summary: handleSummary({ ...state, items: items }),
        };
      }),
    updateItem: (item: Item) =>
      set((state) => {
        const items = state.items.map((i) => (i.id === item.id ? item : i));
        return {
          summary: handleSummary({ ...state, items: items }),
          items: items,
        };
      }),
    upsertItemFromProduct: (params: UpsertItemFromProductParams) =>
      set((state) => {
        const {
          productName,
          productImage,
          price,
          quantity,
          chosenVariants,
          id,
        } = params;

        const existingItem = state.items.find((item) => item.id === id);
        const nextQuantity = (existingItem?.quantity || 0) + quantity;
        const subtotal = price * nextQuantity;
        const description = chosenVariants
          .map((variant) => variant.name)
          .join(", ");

        if (existingItem) {
          const items = state.items.map((item) =>
            item.id === id
              ? {
                  ...item,
                  quantity: nextQuantity,
                  price: { sale: price, subtotal },
                }
              : item
          );
          return {
            items,
            summary: handleSummary({ ...state, items }),
          };
        }

        const newItem: Item = {
          id,
          name: productName,
          description,
          price: { sale: price, subtotal },
          quantity: nextQuantity,
          image: productImage || "",
        };

        const items = [...state.items, newItem];
        return {
          items,
          summary: handleSummary({ ...state, items }),
        };
      }),
    clearItems: () =>
      set((state) => {
        return {
          items: [],
          summary: handleSummary({ ...state, items: [] }),
        };
      }),
    toggleDrawer: () =>
      set((state) => ({
        drawer: { isOpen: !state.drawer.isOpen },
      })),
    closeDrawer: () =>
      set(() => ({
        drawer: { isOpen: false },
      })),
    setDelivery: (delivery: Delivery) =>
      set((state) => ({
        delivery,
        summary: handleSummary({ ...state, delivery }),
      })),
  };
});
